/**
 * Auth API - Authentication endpoints
 */
import apiClient, { setTokens, clearTokens } from './client';

// Types
export interface LoginRequest {
    identifier: string;  // tel or email
    pin: string;
    deviceFingerprint?: string;
    deviceToken?: string;  // Server-issued device ID (for persistent recognition)
    latitude?: number;
    longitude?: number;
}

export interface LoginResponse {
    accessToken?: string;
    refreshToken?: string;
    user?: User;
    // For verification required
    status?: 'verification_required' | '2fa_required' | 'success';
    message?: string;
    deviceId?: string;
    userId?: string; // For 2FA
    challengeId?: string; // OTP challenge ID (camelCase)
    challenge_id?: string; // OTP challenge ID (snake_case from backend)
    serviceKey?: string;
}

export interface User {
    id: string;
    username: string;
    email: string;
    tel?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    role: string;
    isActive: boolean;
    isFrozen: boolean;
    twoFactorEnabled: boolean;
    biometricEnabled: boolean;
    image?: string;
    createdAt: string;
    statut?: string;
}

export interface VerifyOtpRequest {
    deviceId: string;
    otpCode: string;
}

export interface ForgotPinRequest {
    identifier: string;
    serviceKey?: string;
    frontendUrl?: string;
}

export interface ResetPinRequest {
    token: string;
    newPin: string;
}

export interface ChangePinRequest {
    currentPin: string;
    newPin: string;
}

export interface VerifyTransactionPinRequest {
    pin: string;
    transactionId?: string;
    amount?: number;
    transactionType?: string;
}

// API Functions
export const authApi = {
    /**
     * Login with tel/email + PIN
     */
    async login(data: LoginRequest): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/api/auth/login', data);

        // Only store tokens if login was successful (has tokens)
        if (response.data.accessToken && response.data.refreshToken) {
            setTokens(response.data.accessToken, response.data.refreshToken);
        }

        return response.data;
    },

    /**
     * Refresh access token
     */
    async refreshToken(data: { refreshToken: string }): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/api/auth/refresh', data);
        if (response.data.accessToken && response.data.refreshToken) {
            setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    },

    /**
     * Verify OTP for new device
     */
    async verifyOtp(data: VerifyOtpRequest): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/api/auth/verify-otp', data);
        if (response.data.accessToken && response.data.refreshToken) {
            setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    },

    /**
     * Verify OTP for new device (V2)
     */
    async verifyDeviceOtp(challengeId: string, code: string, serviceKey?: string): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/api/auth/device/otp/verify', {
            challengeId,
            code,
            serviceKey: serviceKey || 'bousanm'
        });
        if (response.data.accessToken && response.data.refreshToken) {
            setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    },

    /**
     * Resend device OTP (V2)
     */
    async resendDeviceOtp(email: string, deviceFingerprint: string): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post('/api/auth/device/otp/resend', {
            email,
            deviceFingerprint
        });
        return response.data;
    },

    /**
     * Confirm device from magic link (used by verify-device page)
     */
    async confirmDevice(token: string): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/api/auth/confirm-device', { token });
        if (response.data.accessToken && response.data.refreshToken) {
            setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    },


    /**
     * Verify 2FA TOTP code (during login) and get tokens
     */
    async verify2FALogin(userId: string, deviceId: string, token: string, latitude?: number, longitude?: number): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/api/auth/verify-2fa', { userId, deviceId, token, latitude, longitude });
        if (response.data.accessToken && response.data.refreshToken) {
            setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    },

    /**
     * Verify 2FA token (checking validity only, requires auth)
     */
    async verify2FA(token: string): Promise<boolean> {
        const response = await apiClient.post<{ valid: boolean }>('/api/auth/2fa/verify', { token });
        return response.data.valid;
    },

    /**
     * Get current user profile
     */
    async getProfile(): Promise<User> {
        const response = await apiClient.get<User>('/api/auth/profile');
        return response.data;
    },

    /**
     * Update current user profile
     */
    async updateProfile(data: { firstName?: string; lastName?: string; email?: string; phone?: string; image?: string }): Promise<User> {
        const response = await apiClient.patch<User>('/api/auth/profile', data);
        return response.data;
    },

    /**
     * Upload profile image and get back URL
     */
    async uploadProfileImage(base64Image: string): Promise<{ imagePath: string }> {
        const response = await apiClient.post<{ success: boolean; imagePath: string; message: string }>(
            '/api/upload/profile-image',
            { image: base64Image }
        );
        return { imagePath: response.data.imagePath };
    },

    /**
     * Logout - clear tokens
     */
    logout(): void {
        clearTokens();
    },

    /**
     * Request PIN reset
     */
    async forgotPin(data: ForgotPinRequest): Promise<{ message: string }> {
        const response = await apiClient.post('/api/auth/forgot-pin', {
            ...data,
            frontendUrl: window.location.origin,
        });
        return response.data;
    },

    /**
     * Reset PIN with token
     */
    async resetPin(data: ResetPinRequest): Promise<{ message: string }> {
        const response = await apiClient.post('/api/auth/reset-pin', data);
        return response.data;
    },

    /**
     * Change PIN (requires current PIN)
     */
    async changePin(data: ChangePinRequest): Promise<{ message: string }> {
        const response = await apiClient.post('/api/auth/change-pin', data);
        return response.data;
    },

    /**
     * Verify PIN for transaction (re-authentication)
     */
    async verifyTransactionPin(data: VerifyTransactionPinRequest): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post('/api/auth/verify-transaction-pin', data);
        return response.data;
    },

    // ==========================================
    // 2FA CONFIGURATION
    // ==========================================

    async get2FAStatus(): Promise<{ twoFactorEnabled: boolean }> {
        const response = await apiClient.get('/api/auth/2fa/status');
        return response.data;
    },

    async generate2FASecret(): Promise<{ message: string; secret: string; qrCodeDataUrl: string }> {
        const response = await apiClient.post('/api/auth/2fa/generate');
        return response.data;
    },

    async enable2FA(token: string): Promise<{ message: string; backupCodes: string[] }> {
        const response = await apiClient.post('/api/auth/2fa/enable', { token });
        return response.data;
    },

    async disable2FA(token: string): Promise<{ message: string }> {
        const response = await apiClient.post('/api/auth/2fa/disable', { token });
        return response.data;
    },

    async regenerateBackupCodes(token: string): Promise<{ message: string; backupCodes: string[] }> {
        const response = await apiClient.post('/api/auth/2fa/backup-codes/regenerate', { token });
        return response.data;
    },

    // ==========================================
    // DEVEICE SESSIONS
    // ==========================================

    /**
     * List active sessions (connected devices)
     */
    async listSessions(): Promise<Array<{
        id: string;
        deviceId: string;
        ipAddress: string;
        userAgent: string;
        countryCode?: string;
        city?: string;
        lastActivityAt: string;
        createdAt: string;
    }>> {
        const response = await apiClient.get('/api/sessions');
        return response.data;
    },

    /**
     * Remove a session (logout device)
     */
    async revokeSession(sessionId: string): Promise<{ message: string }> {
        const response = await apiClient.delete(`/api/sessions/${sessionId}`);
        return response.data;
    },

    /**
     * Revoke all sessions
     */
    async revokeAllSessions(): Promise<{ message: string; revokedCount: number }> {
        const response = await apiClient.post('/api/sessions/revoke-all');
        return response.data;
    },

    // ==========================================
    // SECURITY QUESTIONS
    // ==========================================

    async getSecurityQuestions(): Promise<{ id: string; questionText: string }[]> {
        const response = await apiClient.get('/api/security/questions');
        return response.data;
    },

    async getSecurityStatus(): Promise<{ hasSecurityQuestions: boolean; questionsCount: number; requiredQuestions: number }> {
        const response = await apiClient.get('/api/security/questions/status');
        return response.data;
    },

    async getUserQuestions(): Promise<{ questions: { id: string; questionText: string }[] }> {
        const response = await apiClient.get('/api/security/my-questions');
        return response.data;
    },

    async setupSecurityQuestions(answers: Array<{ questionId: string; answer: string }>): Promise<{ message: string }> {
        const response = await apiClient.post('/api/security/setup-questions', { answers });
        return response.data;
    },
};

export default authApi;
